import { API } from '@/lib/api';

// Persistent visitor id (across sessions) + session id (per browsing session, 30 min TTL).
const VID_KEY = 'dubinko_vid';
const SID_KEY = 'dubinko_sid';
const SID_TS_KEY = 'dubinko_sid_ts';
const SESSION_TTL_MS = 30 * 60 * 1000;

function uid() {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) return window.crypto.randomUUID();
  return 'id-' + Math.random().toString(36).slice(2) + Date.now();
}

function getVisitor() {
  let vid = localStorage.getItem(VID_KEY);
  let isNewVisitor = false;
  if (!vid) {
    vid = uid();
    localStorage.setItem(VID_KEY, vid);
    isNewVisitor = true;
  }
  return { vid, isNewVisitor };
}

function getSession() {
  const now = Date.now();
  const ts = parseInt(localStorage.getItem(SID_TS_KEY) || '0', 10);
  let sid = localStorage.getItem(SID_KEY);
  let isNewSession = false;
  if (!sid || !ts || now - ts > SESSION_TTL_MS) {
    sid = uid();
    localStorage.setItem(SID_KEY, sid);
    isNewSession = true;
  }
  localStorage.setItem(SID_TS_KEY, String(now));
  return { sid, isNewSession };
}

function detectDevice() {
  const ua = navigator.userAgent || '';
  if (/iPad|Tablet|PlayBook|Silk|(Android(?!.*Mobile))/i.test(ua)) return 'tablet';
  if (/Mobi|iPhone|iPod|Android.*Mobile|Windows Phone/i.test(ua)) return 'mobile';
  return 'desktop';
}

function referrerSource() {
  try {
    const ref = document.referrer;
    if (!ref) return 'direct';
    const host = new URL(ref).hostname;
    if (host.includes(window.location.hostname)) return 'internal';
    if (/google\./i.test(host)) return 'google';
    if (/facebook\.|fb\./i.test(host)) return 'facebook';
    if (/instagram\./i.test(host)) return 'instagram';
    if (/bing\./i.test(host)) return 'bing';
    return 'other';
  } catch (_) {
    return 'direct';
  }
}

function send(payload) {
  try {
    const url = `${API}/analytics/track`;
    const body = JSON.stringify(payload);
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
    } else {
      fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {});
    }
  } catch (_) { /* fire and forget: analytics must never break UX */ }
}

function baseFields() {
  const { vid, isNewVisitor } = getVisitor();
  const { sid, isNewSession } = getSession();
  return {
    session_id: sid,
    visitor_id: vid,
    is_new_session: isNewSession,
    is_new_visitor: isNewVisitor,
    device: detectDevice(),
    referrer_source: referrerSource(),
  };
}

let lastPath = null;

export function trackPageview(path) {
  if (path === lastPath) return; // avoid duplicate on re-render
  lastPath = path;
  send({ events: [{ ...baseFields(), type: 'pageview', path }] });
}

export function trackEvent(name, meta = {}) {
  send({ events: [{ ...baseFields(), type: 'event', name, path: window.location.pathname, meta }] });
  // Forward to GA if configured, keeping existing marketing behaviour.
  if (typeof window.gtag === 'function') {
    try { window.gtag('event', name, meta); } catch (_) { /* ignore gtag errors */ }
  }
}
